import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, Plus, UploadSimple, DownloadSimple, Trash, PencilSimple, Ruler } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AlimentosPage() {
  const confirmDelete = useConfirmDelete();
  const [foods, setFoods] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [energia, setEnergia] = useState('');
  const [proteina, setProteina] = useState('');
  const [hidratos, setHidratos] = useState('');
  const [gordura, setGordura] = useState('');
  const [fibra, setFibra] = useState('');
  const [acucarTotal, setAcucarTotal] = useState('');
  // Micronutrientes — Item 11.b
  const [calcio, setCalcio] = useState('');
  const [ferro, setFerro] = useState('');
  const [sodio, setSodio] = useState('');
  const [vitC, setVitC] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  // Measures state
  const [measuresFood, setMeasuresFood] = useState(null);
  const [measures, setMeasures] = useState([]);
  const [showAddMeasure, setShowAddMeasure] = useState(false);
  const [mForm, setMForm] = useState({ measure_name: '', grams_equivalent: '', ml_equivalent: '' });

  const fetchFoods = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/foods?search=${search}&limit=100`, { withCredentials: true });
      setFoods(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchFoods(); }, [fetchFoods]);

  const resetForm = () => {
    setName(''); setEnergia(''); setProteina(''); setHidratos(''); setGordura(''); setFibra(''); setAcucarTotal('');
    setCalcio(''); setFerro(''); setSodio(''); setVitC('');
    setEditing(null);
  };

  const openEdit = (food) => {
    setEditing(food);
    setName(food.name);
    setEnergia(String(food.energia_kcal || ''));
    setProteina(String(food.proteina_g || ''));
    setHidratos(String(food.hidratos_g || ''));
    setGordura(String(food.gordura_g || ''));
    setFibra(String(food.fibra_g || ''));
    setAcucarTotal(String(food.acucar_total_g || ''));
    setCalcio(String(food.calcio_mg || ''));
    setFerro(String(food.ferro_mg || ''));
    setSodio(String(food.sodio_mg || ''));
    setVitC(String(food.vit_c_mg || ''));
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name,
      energia_kcal: parseFloat(energia) || 0,
      proteina_g: parseFloat(proteina) || 0,
      hidratos_g: parseFloat(hidratos) || 0,
      gordura_g: parseFloat(gordura) || 0,
      fibra_g: parseFloat(fibra) || 0,
      acucar_total_g: parseFloat(acucarTotal) || 0,
      calcio_mg: parseFloat(calcio) || 0,
      ferro_mg: parseFloat(ferro) || 0,
      sodio_mg: parseFloat(sodio) || 0,
      vit_c_mg: parseFloat(vitC) || 0,
    };
    try {
      if (editing) {
        await axios.put(`${API}/api/foods/${editing.id}`, payload, { withCredentials: true });
        toast.success('Alimento atualizado');
      } else {
        await axios.post(`${API}/api/foods`, payload, { withCredentials: true });
        toast.success('Alimento adicionado');
      }
      setShowForm(false);
      resetForm();
      fetchFoods();
    } catch (err) {
      toast.error('Erro ao guardar alimento');
    }
  };

  const handleDelete = async (id) => {
    const food = foods.find(f => f.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o alimento "${food?.name || ''}" da base de dados alimentar.` });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/foods/${id}`, { withCredentials: true });
      toast.success('Alimento eliminado');
      fetchFoods();
    } catch (err) {
      toast.error('Erro ao eliminar');
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const { data } = await axios.post(`${API}/api/foods/import`, formData, { withCredentials: true });
      toast.success(`Importados: ${data.imported}, Atualizados: ${data.updated}`);
      if (data.errors?.length > 0) {
        toast.error(`${data.errors.length} erros na importacao`);
      }
      setShowImport(false);
      setImportFile(null);
      fetchFoods();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na importacao');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/foods/template`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'template_alimentos.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao descarregar template'); }
  };

  // Measures functions
  const openMeasures = async (food) => {
    setMeasuresFood(food);
    try { const { data } = await axios.get(`${API}/api/foods/${food.id}/measures`); setMeasures(data); } catch { setMeasures([]); }
  };
  const addMeasure = async () => {
    if (!mForm.measure_name || !mForm.grams_equivalent) return;
    try {
      await axios.post(`${API}/api/foods/${measuresFood.id}/measures`, { measure_name: mForm.measure_name, grams_equivalent: parseFloat(mForm.grams_equivalent), ml_equivalent: mForm.ml_equivalent ? parseFloat(mForm.ml_equivalent) : null });
      toast.success('Medida adicionada');
      setShowAddMeasure(false); setMForm({ measure_name: '', grams_equivalent: '', ml_equivalent: '' });
      const { data } = await axios.get(`${API}/api/foods/${measuresFood.id}/measures`); setMeasures(data);
    } catch { toast.error('Erro'); }
  };
  const deleteMeasure = async (measureId) => {
    const m = measures.find(x => x.id === measureId);
    const ok = await confirmDelete({ description: `Vai apagar a medida caseira "${m?.measure_name || ''}" de ${measuresFood?.name || 'este alimento'}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/food-measures/${measureId}`); toast.success('Medida eliminada'); const { data } = await axios.get(`${API}/api/foods/${measuresFood.id}/measures`); setMeasures(data); } catch { toast.error('Erro'); }
  };

  return (
    <div className="fade-in" data-testid="alimentos-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-medium text-xl text-brand-text" data-testid="alimentos-title">Base de Dados Alimentar</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="border border-brand-border text-brand-green hover:bg-brand-green/5 transition-all duration-300 rounded-full px-3 py-2 text-sm font-medium flex items-center gap-1.5"
            data-testid="import-foods-btn"
          >
            <UploadSimple size={16} />
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-brand-green text-white hover:bg-brand-green-hover transition-all duration-300 rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1.5"
            data-testid="add-food-btn"
          >
            <Plus size={16} /> Novo
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-placeholder" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Procurar alimento..."
          className="pl-10 rounded-2xl border-brand-border"
          data-testid="food-search"
        />
      </div>

      {/* Foods list */}
      {loading ? (
        <div className="text-center py-12 text-brand-muted">A carregar...</div>
      ) : foods.length === 0 ? (
        <div className="text-center py-16">
          <MagnifyingGlass size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted">{search ? 'Sem resultados.' : 'Base de dados vazia.'}</p>
          <p className="text-sm text-brand-placeholder mt-1">Adicione alimentos ou importe via CSV/Excel.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {foods.map(food => (
            <div key={food.id} className="bg-white border border-brand-border rounded-2xl p-3 flex items-center justify-between" data-testid={`food-item-${food.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-brand-text truncate">{food.name}</p>
                <div className="flex gap-3 mt-1 text-[11px] text-brand-muted">
                  <span>{food.energia_kcal} kcal</span>
                  <span>P: {food.proteina_g}g</span>
                  <span>HC: {food.hidratos_g}g</span>
                  <span>G: {food.gordura_g}g</span>
                  <span>F: {food.fibra_g}g</span>
                </div>
              </div>
              <div className="flex gap-1 ml-2">
                <button onClick={() => openMeasures(food)} className="p-1.5 text-brand-placeholder hover:text-brand-gold transition-colors" title="Medidas caseiras" data-testid={`measures-food-${food.id}`}>
                  <Ruler size={14} />
                </button>
                <button onClick={() => openEdit(food)} className="p-1.5 text-brand-placeholder hover:text-brand-green transition-colors" data-testid={`edit-food-${food.id}`}>
                  <PencilSimple size={14} />
                </button>
                <button onClick={() => handleDelete(food.id)} className="p-1.5 text-brand-placeholder hover:text-red-500 transition-colors" data-testid={`delete-food-${food.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit food form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">{editing ? 'Editar alimento' : 'Novo alimento'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label className="text-sm text-brand-muted mb-1 block">Nome</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-2xl border-brand-border" required data-testid="food-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-brand-muted mb-1 block">Energia (kcal)</Label>
                <Input type="number" step="0.1" value={energia} onChange={(e) => setEnergia(e.target.value)} className="rounded-2xl border-brand-border" data-testid="food-energia-input" />
              </div>
              <div>
                <Label className="text-sm text-brand-muted mb-1 block">Proteina (g)</Label>
                <Input type="number" step="0.1" value={proteina} onChange={(e) => setProteina(e.target.value)} className="rounded-2xl border-brand-border" data-testid="food-proteina-input" />
              </div>
              <div>
                <Label className="text-sm text-brand-muted mb-1 block">Hidratos (g)</Label>
                <Input type="number" step="0.1" value={hidratos} onChange={(e) => setHidratos(e.target.value)} className="rounded-2xl border-brand-border" data-testid="food-hidratos-input" />
              </div>
              <div>
                <Label className="text-sm text-brand-muted mb-1 block">Gordura (g)</Label>
                <Input type="number" step="0.1" value={gordura} onChange={(e) => setGordura(e.target.value)} className="rounded-2xl border-brand-border" data-testid="food-gordura-input" />
              </div>
              <div>
                <Label className="text-sm text-brand-muted mb-1 block">Fibra (g)</Label>
                <Input type="number" step="0.1" value={fibra} onChange={(e) => setFibra(e.target.value)} className="rounded-2xl border-brand-border" data-testid="food-fibra-input" />
              </div>
              <div>
                <Label className="text-sm text-brand-muted mb-1 block">Açúcar total (g)</Label>
                <Input type="number" step="0.1" value={acucarTotal} onChange={(e) => setAcucarTotal(e.target.value)} className="rounded-2xl border-brand-border" data-testid="food-acucar-input" />
              </div>
              <div>
                <Label className="text-sm text-brand-muted mb-1 block">Cálcio (mg)</Label>
                <Input type="number" step="0.1" value={calcio} onChange={(e) => setCalcio(e.target.value)} className="rounded-2xl border-brand-border" data-testid="food-calcio-input" />
              </div>
              <div>
                <Label className="text-sm text-brand-muted mb-1 block">Ferro (mg)</Label>
                <Input type="number" step="0.01" value={ferro} onChange={(e) => setFerro(e.target.value)} className="rounded-2xl border-brand-border" data-testid="food-ferro-input" />
              </div>
              <div>
                <Label className="text-sm text-brand-muted mb-1 block">Sódio (mg)</Label>
                <Input type="number" step="0.1" value={sodio} onChange={(e) => setSodio(e.target.value)} className="rounded-2xl border-brand-border" data-testid="food-sodio-input" />
              </div>
              <div>
                <Label className="text-sm text-brand-muted mb-1 block">Vitamina C (mg)</Label>
                <Input type="number" step="0.1" value={vitC} onChange={(e) => setVitC(e.target.value)} className="rounded-2xl border-brand-border" data-testid="food-vitc-input" />
              </div>
            </div>
            <button type="submit" className="w-full bg-brand-green text-white hover:bg-brand-green-hover transition-all duration-300 rounded-full px-6 py-3.5 font-medium" data-testid="save-food-btn">
              {editing ? 'Guardar' : 'Adicionar'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Importar Alimentos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-brand-muted">
              Importe alimentos via ficheiro CSV ou Excel (.xlsx). Utilize o template fornecido para garantir a formatação correta.
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center gap-2 text-sm text-brand-green hover:text-brand-green-hover transition-colors"
              data-testid="download-template-btn"
            >
              <DownloadSimple size={16} /> Descarregar template CSV
            </button>
            <div>
              <label className="border-2 border-dashed border-brand-mint rounded-3xl p-6 flex flex-col items-center justify-center text-brand-green bg-brand-mint/10 hover:bg-brand-mint/20 cursor-pointer transition-colors">
                <UploadSimple size={24} weight="duotone" />
                <span className="text-xs mt-2">{importFile ? importFile.name : 'Selecionar ficheiro CSV ou XLSX'}</span>
                <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} className="hidden" data-testid="import-file-input" />
              </label>
            </div>
            <button
              onClick={handleImport}
              disabled={!importFile || importing}
              className="w-full bg-brand-gold text-white hover:bg-brand-gold-hover transition-all duration-300 rounded-full px-6 py-3.5 font-medium disabled:opacity-50"
              data-testid="submit-import-btn"
            >
              {importing ? 'A importar...' : 'Importar'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Measures dialog */}
      <Dialog open={!!measuresFood} onOpenChange={() => setMeasuresFood(null)}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[80vh] overflow-y-auto">
          {measuresFood && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-brand-text">Medidas caseiras: {measuresFood.name}</DialogTitle>
              </DialogHeader>

              {measures.length === 0 ? (
                <p className="text-xs text-brand-muted text-center py-4">Sem medidas caseiras associadas.</p>
              ) : (
                <div className="space-y-2 mb-4">
                  {measures.map(m => (
                    <div key={m.id} className="flex items-center justify-between p-2.5 bg-brand-bg/50 rounded-xl border border-brand-border/40">
                      <div>
                        <p className="text-sm text-brand-text">{m.measure_name}</p>
                        <p className="text-[10px] text-brand-muted">{m.grams_equivalent}g{m.ml_equivalent ? ` / ${m.ml_equivalent}ml` : ''}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {m.from_food && <span className="text-[9px] bg-brand-gold/10 text-brand-gold-hover px-1.5 py-0.5 rounded-full">herdada de {m.from_food}</span>}
                        {!m.from_food && <button onClick={() => deleteMeasure(m.id)} className="p-1 text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add measure form */}
              {showAddMeasure ? (
                <div className="bg-white border border-brand-border rounded-xl p-3 space-y-2">
                  <div><Label className="text-[10px] text-brand-muted mb-0.5 block">Nome da medida</Label><Input value={mForm.measure_name} onChange={e => setMForm({...mForm, measure_name: e.target.value})} placeholder="Ex: chávena, fatia, unidade" className="rounded-lg border-brand-border text-sm h-8" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px] text-brand-muted mb-0.5 block">Gramas</Label><Input type="number" value={mForm.grams_equivalent} onChange={e => setMForm({...mForm, grams_equivalent: e.target.value})} className="rounded-lg border-brand-border text-sm h-8" /></div>
                    <div><Label className="text-[10px] text-brand-muted mb-0.5 block">ml (opcional)</Label><Input type="number" value={mForm.ml_equivalent} onChange={e => setMForm({...mForm, ml_equivalent: e.target.value})} className="rounded-lg border-brand-border text-sm h-8" /></div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={addMeasure} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium">Guardar</button>
                    <button onClick={() => setShowAddMeasure(false)} className="text-xs text-brand-muted">Cancelar</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setMForm({ measure_name: '', grams_equivalent: '', ml_equivalent: '' }); setShowAddMeasure(true); }} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-1" data-testid="add-measure-btn">
                  <Plus size={14} /> Adicionar medida
                </button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
